import React, { useState, useEffect } from 'react';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Box, MenuItem, Select, TextField, Tooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEye,
    faPenToSquare,
    faTrashCan,
    faSave,
} from '@fortawesome/free-solid-svg-icons';
import { STAGE_ITEMS } from '../Constants/dropdownItems';
import createNotification from '../../../utils/createNotification';
import LinkActionPopup, {
    LinkActionPopupMode,
} from '../LinkActionPopup/LinkActionPopup';
import './TimelineTable.scss';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';
import { TimelineValidationHelper } from './TimelineValidationHelper';
import { DocumentDetails } from '../../../slices/marketplace/timeline/models/document-details.model';
import {
    TimelineEventStatus,
    TimelineEventStatuses,
    TimelineEventsTransitions,
} from '../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import {
    TimelineParties,
    TimelineParty,
} from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { TimelineActionType } from '../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TimelineAction } from '../../../slices/marketplace/timeline/models/timeline-actions.model';
import { TimelineEventDependency } from '../../../slices/marketplace/timeline/models/timeline-event-dependency.model';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import { TimelineStage } from '../../../slices/marketplace/timeline/models/timeline-stage.model';
import { DocumentSection } from '../../../slices/marketplace/timeline/models/document-section.model';
import { EngagementDocumentSource } from '../../../slices/marketplace/timeline/models/enums/engagement-document-source';
import { ActionDocumentType } from '../../../slices/marketplace/timeline/models/enums/action-document-type.enum';
import { TimelineActionStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-action-status.enum';
import { DocumentDetailsStatus } from '../../../slices/marketplace/timeline/models/enums/document-details-status.enum';
import TimelineDependencyChips from '../../../components/TimelineDependencyChips/TimelineDependencyChips';
import EventDependencyEditorCell from './EventDependencyEditorCell/EventDependencyEditorCell';
import { TeaserPracticeData } from '../../../slices/marketplace/timeline/models/teaser-data.model';
import { LinkedDataRequestProject } from '../../../slices/marketplace/timeline/models/linked-data-request-project.model';
import { NdaTermSelectionDocumentTemplatesState } from '../../../slices/marketplace/timeline/models/nda-term-selection';
import { DocumentDetailsContentType } from '../../../slices/marketplace/timeline/models/enums/document-details-content-type';
import { TimelineTempo } from '../../../slices/marketplace/timeline/models/enums/timeline-tempo.enum';
import { BuyerExpressionOfInterestSettings } from '../../../slices/marketplace/timeline/models/buyer-expression-of-interest-settings.model';
import { LevelOfService } from '../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { generateGuid } from '../../../utils/typedHelper';

interface TimelineTableProps {
    timelineId: number;
    rows: TimelineEvent[];
    stages: TimelineStage[];
    handleRemoveRow: (id: string) => void;
    handleUpdateRow: (
        id: string,
        updatedValues: {
            name?: string;
            duration?: number;
            stage?: number;
            party?: number;
            dependencies?: TimelineEventDependency[];
            linkedAction?: TimelineAction;
            status: TimelineEventStatus;
        },
        documentDetails?: DocumentDetails,
        documentToRemove?: DocumentDetails,
        additionalDocumentDetails?: DocumentDetails[],
        additionalDocumentsToRemove?: DocumentDetails[],
        dataRequestProject?: LinkedDataRequestProject,
        ndaTermSelection?: NdaTermSelectionDocumentTemplatesState,
        expressionOfInterestSettings?: BuyerExpressionOfInterestSettings
    ) => boolean;
    documentDetailsList: DocumentDetails[];
    dataRequestProjects: LinkedDataRequestProject[];
    documentsSections: DocumentSection[];
    setDocumentSectionList: (documentSections: DocumentSection[]) => void;
    teaserData: TeaserPracticeData[];
    onTeaserSave: (newValues: TeaserPracticeData[]) => Promise<void>;
    onUpdateConfirm: () => void;
    isTeaserConfirmed: boolean;
    isLoading: boolean;
    isArchived: boolean;
    isTemplate: boolean;
    ndaTermSelectionState: NdaTermSelectionDocumentTemplatesState;
    currentTempo: TimelineTempo;
    expressionOfInterestSettings: BuyerExpressionOfInterestSettings;
    levelOfService: LevelOfService;
}

const TimelineTable = (props: TimelineTableProps) => {
    const [eyeActionIsDisabled, setEyeActionIsDisabled] = useState(true);
    const [isDependencySelectorVisible, setIsDependencySelectorVisible] =
        useState(false);
    const [selectedDependencyId, setSelectedDependencyId] = useState('');
    const [selectedDependencyIdForRemoval, setSelectedDependencyIdForRemoval] =
        useState('');
    const [selectedDependencyOffsetDays, setSelectedDependencyOffsetDays] =
        useState(0);

    const [editRowId, setEditRowId] = useState<string | null>(null);
    const [editedEvent, setEditedEvent] = useState<TimelineEvent>();
    const [linkActionRowId, setLinkActionRowId] = useState<string | null>(null);

    useEffect(() => {
        setEditRowId(null);
        setEditedEvent(undefined);
    }, [props.currentTempo]);
    const [actionEvent, setActionEvent] = useState<TimelineEvent>();
    const [isLinkActionPopupVisible, setIsLinkActionPopupVisible] =
        useState(false);
    const [statusToConfirm, setStatusToConfirm] = useState(false);
    const [linkActionPopupMode, setLinkActionPopupMode] =
        useState<LinkActionPopupMode>('create');
    const [
        isConfirmationActionRemovalVisible,
        setIsConfirmationActionRemovalVisible,
    ] = useState<boolean>(false);
    const [
        isConfirmationEventRemovalVisible,
        setIsConfirmationEventRemovalVisible,
    ] = useState<boolean>(false);
    const [
        isConfirmationDependencyRemovalVisible,
        setIsConfirmationDependencyRemovalVisible,
    ] = useState<boolean>(false);
    const [selectedEventToRemoveId, setSelectedEventToRemoveId] = useState('');

    const generateStageCode = (index: number): string => {
        const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        let code = '';

        while (index >= 0) {
            code = alphabet[index % 26] + code;
            index = Math.floor(index / 26) - 1;
        }

        return code;
    };

    const isMeetingActionType = (actionType: TimelineActionType): boolean => {
        return (
            actionType ===
                TimelineActionType.SellerProvideMeetingAvailability ||
            actionType === TimelineActionType.SellerBuyerAgreeOnMeetingTimes ||
            actionType === TimelineActionType.SellerBuyerMeetingsPeriod
        );
    };

    const shouldPreventMeetingActionsEditOrDelete = (): boolean => {
        const meetingActionEvents = props.rows.filter(
            (event) =>
                event.linkedAction &&
                isMeetingActionType(event.linkedAction.actionType)
        );

        if (meetingActionEvents.length === 0) {
            return false;
        }

        const hasNonUpcomingMeetingAction = meetingActionEvents.some(
            (event) => event.status !== TimelineEventStatus.Upcoming
        );

        return hasNonUpcomingMeetingAction;
    };

    const canEventBeEditedOrDeleted = (event: TimelineEvent): boolean => {
        if (
            !event.linkedAction ||
            !isMeetingActionType(event.linkedAction.actionType)
        ) {
            return true;
        }

        return !shouldPreventMeetingActionsEditOrDelete();
    };

    const isFieldDisabledForMeetingAction = (event: TimelineEvent): boolean => {
        if (
            !event.linkedAction ||
            !isMeetingActionType(event.linkedAction.actionType)
        ) {
            return false;
        }

        return shouldPreventMeetingActionsEditOrDelete();
    };

    const isMeetingActionEditOrDeleteDisabled = (
        action: TimelineAction | undefined
    ): boolean => {
        if (!action || !isMeetingActionType(action.actionType)) {
            return false;
        }

        return shouldPreventMeetingActionsEditOrDelete();
    };

    const handleEventEditClick = (row: TimelineEvent) => {
        setEditRowId(row.id);
        setEditedEvent(row);
        setSelectedDependencyId('');
        setSelectedDependencyOffsetDays(0);
        setIsDependencySelectorVisible(false);
    };

    const handleEventSaveClick = (rowId: string) => {
        if (editedEvent) {
            const isTempoDurationValid =
                TimelineValidationHelper.areTemposDurationsValid(
                    editedEvent.slowTempoDurationDays,
                    editedEvent.mediumTempoDurationDays,
                    editedEvent.fastTempoDurationDays
                );

            if (!isTempoDurationValid.isValid) {
                createNotification(isTempoDurationValid.errorMessage, 'error');
                return;
            }

            const selectedStageId = editedEvent.stage;
            const stageExists = props.stages.some(
                (stage) => stage.id === selectedStageId
            );

            if (!stageExists) {
                createNotification(
                    'Selected stage does not exist in the list of stages',
                    'error'
                );
                return;
            }

            const originalEvent = props.rows.find((f) => f.id === rowId);
            const isPartyChanged = originalEvent.party !== editedEvent.party;
            const masterDocumentToRemove = getDocumentDetailsFromAction(
                originalEvent.linkedAction
            );
            const additionalDocsToRemove = getAdditionalDocDetailsFromMaster(
                masterDocumentToRemove
            );

            const eventBecameCompleted =
                originalEvent.status !== TimelineEventStatus.Completed &&
                editedEvent.status === TimelineEventStatus.Completed;

            const eventBecameNotUpcomming =
                originalEvent.status === TimelineEventStatus.Upcoming &&
                editedEvent.status !== TimelineEventStatus.Upcoming;

            const sellerTeaserAction =
                !!originalEvent.linkedAction &&
                originalEvent.linkedAction?.actionType ===
                    TimelineActionType.SellerTeaserApproval;

            const sellerNeedsConfirmationDocAction =
                !!originalEvent.linkedAction &&
                originalEvent.linkedAction?.actionType ===
                    TimelineActionType.SellerEngagementDocument &&
                !!masterDocumentToRemove &&
                masterDocumentToRemove.type ===
                    ActionDocumentType.DocumentWithFeedback;

            const buyerSfasExpressionOfInterestAction =
                !!originalEvent.linkedAction &&
                originalEvent.linkedAction?.actionType ===
                    TimelineActionType.BuyerExpressionOfInterest &&
                props.levelOfService ===
                    LevelOfService.StrategicFinancialAdvisoryService;

            const needsConfirmationFileDoc =
                sellerNeedsConfirmationDocAction &&
                !!masterDocumentToRemove.fileName;

            const questionnairePIQDoc =
                sellerNeedsConfirmationDocAction &&
                masterDocumentToRemove.source ==
                    EngagementDocumentSource.Questionnaire;

            const completedAction =
                !isPartyChanged &&
                eventBecameCompleted &&
                (sellerTeaserAction ||
                    sellerNeedsConfirmationDocAction ||
                    buyerSfasExpressionOfInterestAction)
                    ? {
                          ...originalEvent.linkedAction,
                          status: TimelineActionStatus.Completed,
                      }
                    : undefined;

            const completedMasterDocDetails =
                !!completedAction &&
                !!masterDocumentToRemove &&
                !!needsConfirmationFileDoc &&
                !questionnairePIQDoc
                    ? {
                          ...masterDocumentToRemove,
                          status: DocumentDetailsStatus.Completed,
                      }
                    : undefined;
            const completedAdditionalDocDetails =
                !!completedMasterDocDetails && additionalDocsToRemove.length > 0
                    ? additionalDocsToRemove.map((d) => {
                          return {
                              ...d,
                              status: DocumentDetailsStatus.Completed,
                          };
                      })
                    : [];

            const dependanciesWithSameOffsets: TimelineEventDependency[] =
                editedEvent.dependencies.map((d) => {
                    return {
                        ...d,
                        slowTempoOffsetDays: d.currentTempoOffsetDays,
                        mediumTempoOffsetDays: d.currentTempoOffsetDays,
                        fastTempoOffsetDays: d.currentTempoOffsetDays,
                    };
                });

            const eventWithSameDurationAndOffsets: TimelineEvent = {
                ...editedEvent,
                dependencies: dependanciesWithSameOffsets,
                slowTempoDurationDays: editedEvent.currentTempoDurationDays,
                mediumTempoDurationDays: editedEvent.currentTempoDurationDays,
                fastTempoDurationDays: editedEvent.currentTempoDurationDays,
            };

            const newEvent = eventBecameNotUpcomming
                ? eventWithSameDurationAndOffsets
                : editedEvent;

            if (
                props.handleUpdateRow(
                    rowId,
                    {
                        ...newEvent,
                        linkedAction: isPartyChanged
                            ? null
                            : completedAction ?? editedEvent.linkedAction,
                    },
                    !!completedMasterDocDetails
                        ? completedMasterDocDetails
                        : null,
                    isPartyChanged ? masterDocumentToRemove : null,
                    completedAdditionalDocDetails,
                    isPartyChanged ? additionalDocsToRemove : null,
                    editedEvent.linkedAction?.actionType ===
                        TimelineActionType.SellerDataAndDocumentRequest
                        ? props.dataRequestProjects.find(
                              (p) =>
                                  p.id ===
                                  editedEvent.linkedAction?.referenceItemId
                          )
                        : undefined,
                    undefined,
                    props.expressionOfInterestSettings
                )
            ) {
                setEditRowId(null);
                setEditedEvent(null);
            }
        }
    };

    const handleInputChange = (field: string, value: string | number) => {
        let isValid = true;
        if (field === 'stage') {
            isValid = TimelineValidationHelper.isNewStageActionValid(
                value as number,
                editedEvent,
                props.rows
            );
        }

        if (isValid) {
            let updatedEvent: TimelineEvent = { ...editedEvent };

            if (field == 'duration') {
                const isSlow =
                    !editedEvent.currentTempo ||
                    (!!editedEvent.currentTempo &&
                        editedEvent.currentTempo == props.currentTempo &&
                        props.currentTempo == TimelineTempo.SLOW);
                const isMedium =
                    !editedEvent.currentTempo ||
                    (!!editedEvent.currentTempo &&
                        editedEvent.currentTempo == props.currentTempo &&
                        props.currentTempo == TimelineTempo.MEDIUM);
                const isFast =
                    !editedEvent.currentTempo ||
                    (!!editedEvent.currentTempo &&
                        editedEvent.currentTempo == props.currentTempo &&
                        props.currentTempo == TimelineTempo.FAST);

                const newDuration = Number(value);

                updatedEvent = {
                    ...updatedEvent,
                    slowTempoDurationDays: isSlow
                        ? newDuration || 0
                        : updatedEvent.slowTempoDurationDays,
                    mediumTempoDurationDays: isMedium
                        ? newDuration || 0
                        : updatedEvent.mediumTempoDurationDays,
                    fastTempoDurationDays: isFast
                        ? newDuration || 0
                        : updatedEvent.fastTempoDurationDays,
                    currentTempoDurationDays: newDuration,
                };
            } else {
                updatedEvent = {
                    ...updatedEvent,
                    [field]: value,
                };
            }

            setEditedEvent(updatedEvent);
        }
    };

    const handleAddDependency = () => {
        const doesSelectedDependencyExist = props.rows.some(
            (row: TimelineEvent) => row.id === selectedDependencyId
        );
        if (!doesSelectedDependencyExist) {
            return undefined;
        }

        const newDependency: TimelineEventDependency = {
            dependencyEventId: selectedDependencyId,
            slowTempoOffsetDays: selectedDependencyOffsetDays,
            mediumTempoOffsetDays: selectedDependencyOffsetDays,
            fastTempoOffsetDays: selectedDependencyOffsetDays,
            currentTempoOffsetDays: selectedDependencyOffsetDays,
        };

        const resultDependencies = [
            ...(editedEvent?.dependencies || []),
            newDependency,
        ];

        setEditedEvent((prev) => ({
            ...prev,
            dependencies: [...(prev?.dependencies || []), newDependency],
        }));

        setIsDependencySelectorVisible(false);
        setSelectedDependencyId('');
        setSelectedDependencyOffsetDays(0);

        return resultDependencies;
    };

    const handleRemoveDependencyById = (dependencyId: string) => {
        setEditedEvent((prev) => ({
            ...prev,
            dependencies: prev.dependencies.filter(
                (dependency) => dependency.dependencyEventId !== dependencyId
            ),
        }));

        setSelectedDependencyIdForRemoval('');
    };

    const handleDependencyOffsetDaysChange = (
        dependencyEventId: string,
        newOffset: number
    ) => {
        setEditedEvent((prev) => ({
            ...prev,
            dependencies: prev.dependencies.map((dep) => {
                const isSlow =
                    !editedEvent.currentTempo ||
                    (!!editedEvent.currentTempo &&
                        editedEvent.currentTempo == props.currentTempo &&
                        props.currentTempo == TimelineTempo.SLOW);
                const isMedium =
                    !editedEvent.currentTempo ||
                    (!!editedEvent.currentTempo &&
                        editedEvent.currentTempo == props.currentTempo &&
                        props.currentTempo == TimelineTempo.MEDIUM);
                const isFast =
                    !editedEvent.currentTempo ||
                    (!!editedEvent.currentTempo &&
                        editedEvent.currentTempo == props.currentTempo &&
                        props.currentTempo == TimelineTempo.FAST);

                return dep.dependencyEventId === dependencyEventId
                    ? {
                          ...dep,
                          slowTempoOffsetDays: isSlow
                              ? newOffset
                              : dep.slowTempoOffsetDays,
                          mediumTempoOffsetDays: isMedium
                              ? newOffset
                              : dep.mediumTempoOffsetDays,
                          fastTempoOffsetDays: isFast
                              ? newOffset
                              : dep.fastTempoOffsetDays,
                          currentTempoOffsetDays: newOffset,
                      }
                    : dep;
            }),
        }));
    };

    const handleRemoveRowWithDependencies = (id: string) => {
        if (editedEvent?.dependencies) {
            const updatedDependencies = editedEvent.dependencies.filter(
                (dependency) => dependency.dependencyEventId !== id
            );
            setEditedEvent((prev) => ({
                ...prev,
                dependencies: updatedDependencies,
            }));
        }

        setSelectedEventToRemoveId('');
        props.handleRemoveRow(id);
    };

    const confirmDependency = () => {
        if (selectedDependencyId && selectedDependencyOffsetDays > -1) {
            handleAddDependency();
        }
    };

    const rootStyles = STAGE_ITEMS.reduce((styles, stage, index) => {
        const code = generateStageCode(index);
        styles[`& .${code}`] = {
            backgroundColor: stage.color,
            display: 'flex',
            justifyContent: 'center',
        };
        return styles;
    }, {} as Record<string, React.CSSProperties>);

    const stageMap = new Map();

    const additionalStyles = {
        border: 'none',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '.MuiDataGrid-scrollbar': {
            display: 'none',
        },
        '.MuiDataGrid-row': {
            borderBottom: 'none',
            '&:nth-of-type(odd)': {
                backgroundColor: '#f9f9f9',
            },
            '&:nth-of-type(even)': {
                backgroundColor: '#ffffff',
            },
        },
        '.MuiDataGrid-columnHeader:focus': {
            outline: 'none',
        },
        '.MuiDataGrid-cell:focus': {
            outline: 'none',
        },
        '.input-border .MuiOutlinedInput-input': {
            border: '1px #dce0e4 solid',
            borderRadius: '4px',
            background: 'white',
        },
        '& .MuiDataGrid-filler': {
            height: '6px !important',
        },
    };

    const handleAddDependencyClick = () => {
        setIsDependencySelectorVisible(true);
    };

    const getDependenciesChipsEditor = (params: GridRenderCellParams) => {
        const availableDependencies = props.rows.filter(
            (row: TimelineEvent) =>
                row.id !== params.row.id &&
                editedEvent?.dependencies?.every(
                    (dep: TimelineEventDependency) =>
                        dep.dependencyEventId !== row.id
                )
        );

        return (
            <EventDependencyEditorCell
                editedEvent={editedEvent}
                allEvents={props.rows}
                availableDependencies={availableDependencies}
                isDependencySelectorVisible={isDependencySelectorVisible}
                selectedDependencyId={selectedDependencyId}
                setSelectedDependencyId={setSelectedDependencyId}
                setSelectedDependencyIdForRemoval={
                    setSelectedDependencyIdForRemoval
                }
                selectedDependencyOffsetDays={selectedDependencyOffsetDays}
                setSelectedDependencyOffsetDays={
                    setSelectedDependencyOffsetDays
                }
                handleAddDependencyClick={handleAddDependencyClick}
                confirmDependency={confirmDependency}
                setIsConfirmationDependencyRemovalVisible={
                    setIsConfirmationDependencyRemovalVisible
                }
                onOffsetDaysChange={handleDependencyOffsetDaysChange}
                isDisabled={isFieldDisabledForMeetingAction(editedEvent)}
            />
        );
    };

    const getDependenciesChipsView = (params: GridRenderCellParams) => {
        return (
            <>
                {params.row.dependencies.length > 0 && (
                    <Box
                        sx={{
                            width: '100%',
                            gap: '0.5rem',
                            display: 'flex',
                            flexDirection: 'column',
                        }}>
                        {params.row.dependencies.map(
                            (dep: TimelineEventDependency, index: number) => {
                                const isSuspended = props.rows.find(
                                    (row: TimelineEvent) =>
                                        row.id === dep.dependencyEventId
                                )?.isSuspendEvent;
                                if (isSuspended === true) {
                                    return <></>;
                                }
                                const eventName = props.rows.find(
                                    (row: TimelineEvent) =>
                                        row.id === dep.dependencyEventId
                                )?.name;

                                const eventIndex = props.rows.findIndex(
                                    (row: TimelineEvent) =>
                                        row.id === dep.dependencyEventId
                                );

                                return (
                                    <TimelineDependencyChips
                                        index={index}
                                        eventIndex={eventIndex}
                                        eventName={eventName}
                                        offsetDays={dep.currentTempoOffsetDays}
                                        isRemovable={false}
                                        isEditMode={false}
                                    />
                                );
                            }
                        )}
                    </Box>
                )}
            </>
        );
    };

    const finalStyles = { ...rootStyles, ...additionalStyles };

    const columns: GridColDef[] = [
        {
            field: 'index',
            headerName: 'Index',
            headerClassName: 'text-bold',
            width: 58,
            renderCell: (params) =>
                params.api.getAllRowIds().indexOf(params.id) + 1,
        },
        {
            field: 'stage',
            headerName: 'Stage',
            width: 150,
            maxWidth: 200,
            headerAlign: 'center',
            renderCell: (params) => {
                if (params.row.id === editRowId) {
                    return (
                        <Select
                            disabled={
                                editedEvent.status !==
                                    TimelineEventStatus.Upcoming ||
                                isFieldDisabledForMeetingAction(editedEvent)
                            }
                            value={editedEvent?.stage || ''}
                            onChange={(e) =>
                                handleInputChange('stage', e.target.value)
                            }
                            className="input-border"
                            size="small"
                            fullWidth
                            sx={{
                                lineHeight: '1.5',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                },
                            }}>
                            {props.stages.map((stage) => (
                                <MenuItem key={stage.id} value={stage.id}>
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            width: '100%',
                                            maxWidth: '400px',
                                        }}>
                                        <span
                                            style={{
                                                display: 'inline-block',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                            title={stage.name}>
                                            {stage.name}
                                        </span>
                                    </span>
                                </MenuItem>
                            ))}
                        </Select>
                    );
                }

                const stageId = params.value;
                const stage = props.stages.find(
                    (s: TimelineStage) => s.id === stageId
                );
                const name = stage?.name || 'N/A';

                if (!stageMap.has(stageId)) {
                    const index = stageMap.size;
                    const code = generateStageCode(index);
                    const color =
                        index < STAGE_ITEMS.length
                            ? STAGE_ITEMS[index].color
                            : null;

                    stageMap.set(stageId, { code, color });
                }

                const mappedStage = stageMap.get(stageId);
                const code = mappedStage?.code || 'N/A';
                const colorStyle = mappedStage?.color
                    ? { backgroundColor: mappedStage.color }
                    : {};

                return (
                    <Tooltip title={name}>
                        <span
                            className="w-100 d-flex justify-content-center align-items-center"
                            style={{ ...colorStyle }}>
                            {code}
                        </span>
                    </Tooltip>
                );
            },
            cellClassName: (params) => {
                const stageId = params.value;

                if (!stageMap.has(stageId)) {
                    const index = stageMap.size;
                    const code = generateStageCode(index);
                    const color =
                        index < STAGE_ITEMS.length
                            ? STAGE_ITEMS[index].color
                            : null;

                    stageMap.set(stageId, { code, color });
                }

                const baseClasses = 'center-align cursor-pointer';
                const stage = stageMap.get(stageId);
                return stage?.color
                    ? `${stage.code} ${baseClasses}`
                    : baseClasses;
            },
        },
        {
            field: 'name',
            headerName: 'Event Name',
            minWidth: 185,
            width: 300,
            renderCell: (params) =>
                params.row.id === editRowId ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            padding: '0 8px',
                        }}>
                        <TextField
                            className="input-border"
                            value={editedEvent?.name || ''}
                            onChange={(e) =>
                                handleInputChange('name', e.target.value)
                            }
                            onKeyDown={(e) => {
                                if (e.key === ' ') {
                                    e.stopPropagation();
                                }
                            }}
                            size="small"
                            fullWidth
                            slotProps={{
                                input: {
                                    style: {
                                        lineHeight: '1.5',
                                    },
                                },
                            }}
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                },
                            }}
                        />
                    </Box>
                ) : (
                    params.row.name
                ),
        },
        {
            field: 'party',
            headerName: 'Party',
            width: 140,
            minWidth: 140,
            renderCell: (params) => {
                if (params.row.id === editRowId) {
                    return (
                        <Select
                            className="input-border"
                            disabled={
                                editedEvent?.status !==
                                    TimelineEventStatus.Upcoming ||
                                isFieldDisabledForMeetingAction(editedEvent)
                            }
                            value={editedEvent?.party}
                            onChange={(e) => {
                                handleInputChange('party', e.target.value);
                            }}
                            size="small"
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                },
                            }}>
                            {TimelineParties.filter(
                                (party) => party.id !== TimelineParty.Broker
                            ).map((party) => (
                                <MenuItem key={party.id} value={party.id}>
                                    {party.name}
                                </MenuItem>
                            ))}
                        </Select>
                    );
                }
                const party = TimelineParties.find(
                    (item) => item.id === params.value
                );
                return party ? party.name : '-';
            },
        },
        {
            field: 'dependencies',
            headerName: 'Dependencies',
            minWidth: 260,
            width: 300,
            cellClassName: 'dependencies-cell',
            renderCell: (params) => {
                if (params.row.id === editRowId) {
                    return getDependenciesChipsEditor(params);
                }

                return getDependenciesChipsView(params);
            },
        },
        {
            field: 'duration',
            headerName: 'Event Duration',
            width: 130,
            renderCell: (params: GridRenderCellParams<TimelineEvent>) => {
                if (params.row.id === editRowId) {
                    const editedEventActualDuration = !!editedEvent.dayCompleted
                        ? editedEvent.dayCompleted - editedEvent.dayStart
                        : editedEvent.currentTempoDurationDays;

                    return (
                        <Box
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                height: '100%',
                                padding: '0 8px',
                            }}>
                            <TextField
                                className="input-border"
                                disabled={
                                    editedEvent.status !==
                                        TimelineEventStatus.Upcoming ||
                                    isFieldDisabledForMeetingAction(editedEvent)
                                }
                                type="number"
                                value={editedEventActualDuration ?? 0}
                                onChange={(e) =>
                                    handleInputChange(
                                        'duration',
                                        e.target.value
                                    )
                                }
                                size="small"
                                slotProps={{
                                    input: {
                                        style: {
                                            lineHeight: '1.5',
                                            padding: '8px 12px',
                                        },
                                    },
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                            />
                        </Box>
                    );
                }

                const actualDuration = !!params.row.dayCompleted
                    ? params.row.dayCompleted - params.row.dayStart
                    : params.row.currentTempoDurationDays;

                return actualDuration;
            },
        },
        { field: 'dayStart', headerName: 'Start Day', width: 100 },
        {
            field: 'dayEnd',
            headerName: 'End Day',
            width: 100,
            renderCell: (params: GridRenderCellParams<TimelineEvent>) => {
                return params.row.dayCompleted ?? params.row.dayEnd;
            },
        },
        {
            field: 'linkedAction',
            headerName: 'Linked Action',
            width: 120,
            renderCell: (params: GridRenderCellParams<TimelineEvent>) => {
                const targetEvent =
                    editedEvent?.id === params.row.id
                        ? editedEvent
                        : params.row;

                if (targetEvent.isSuspendEvent) {
                    return <></>;
                }
                if (targetEvent.linkedAction) {
                    return (
                        <div
                            className={'link-text'}
                            onClick={() => {
                                setLinkActionRowId(targetEvent.id);
                                setActionEvent(targetEvent);
                                setLinkActionPopupMode('preview');
                                setIsLinkActionPopupVisible(true);
                            }}>
                            {'Preview Action'}
                        </div>
                    );
                }

                if (
                    targetEvent.status !== TimelineEventStatus.Upcoming ||
                    isFieldDisabledForMeetingAction(targetEvent)
                ) {
                    return <></>;
                }

                return props.isArchived ? (
                    <></>
                ) : (
                    <div
                        className={'link-text'}
                        onClick={() => {
                            setLinkActionRowId(targetEvent.id);
                            setActionEvent(targetEvent);
                            setLinkActionPopupMode('create');
                            setIsLinkActionPopupVisible(true);
                        }}>
                        {'Add Action'}
                    </div>
                );
            },
        },
        ...(!props.isTemplate
            ? [
                  {
                      field: 'status',
                      headerName: 'Status',
                      width: 145,
                      minWidth: 145,
                      renderCell: (
                          params: GridRenderCellParams<TimelineEvent>
                      ) => {
                          if (params.row.id === editRowId) {
                              const eventBeforeUpdate = props.rows.find(
                                  (p) => p.id == editRowId
                              );
                              const statusBefore = eventBeforeUpdate.status;
                              return (
                                  <Select
                                      className="input-border"
                                      value={editedEvent?.status}
                                      onChange={(e) => {
                                          if (
                                              e.target.value ===
                                                  TimelineEventStatus.Completed &&
                                              statusBefore !==
                                                  TimelineEventStatus.Completed
                                          ) {
                                              if (
                                                  !props.rows.some(
                                                      (r) =>
                                                          r.status !==
                                                              TimelineEventStatus.Completed &&
                                                          r.id !== editRowId
                                                  )
                                              ) {
                                                  setStatusToConfirm(true);
                                                  return;
                                              }
                                          }
                                          handleInputChange(
                                              'status',
                                              e.target.value
                                          );
                                      }}
                                      size="small"
                                      fullWidth
                                      disabled={
                                          statusBefore ===
                                          TimelineEventStatus.Completed
                                      }
                                      sx={{
                                          '& .MuiOutlinedInput-notchedOutline':
                                              {
                                                  border: 'none',
                                              },
                                      }}>
                                      {TimelineEventStatuses.filter(
                                          (s) =>
                                              TimelineEventsTransitions.get(
                                                  statusBefore
                                              ).has(s.id) &&
                                              ((s.id !==
                                                  TimelineEventStatus.InProgress &&
                                                  s.id !==
                                                      TimelineEventStatus.Completed) ||
                                                  canEventBeActivated(
                                                      editRowId
                                                  ))
                                      ).map((status) => (
                                          <MenuItem
                                              key={status.id}
                                              value={status.id}>
                                              {status.name}
                                          </MenuItem>
                                      ))}
                                  </Select>
                              );
                          }
                          const status = TimelineEventStatuses.find(
                              (item) => item.id === params.value
                          );
                          return status ? status.name : '-';
                      },
                  },
              ]
            : []),
        {
            field: 'action',
            headerName: 'Actions',
            width: 100,
            renderCell: (params) => (
                <div className="action-cell">
                    <div className="d-flex justify-content-between align-items-center">
                        {params.row.isSuspendEvent ? (
                            <></>
                        ) : (
                            <>
                                <FontAwesomeIcon
                                    className={`${
                                        eyeActionIsDisabled
                                            ? 'text-muted'
                                            : 'cursor-pointer'
                                    }`}
                                    size="lg"
                                    icon={faEye}
                                />
                                {!props.isArchived ? (
                                    <>
                                        {params.row.id === editRowId ? (
                                            <FontAwesomeIcon
                                                icon={faSave}
                                                className="cursor-pointer"
                                                size="lg"
                                                onClick={() =>
                                                    handleEventSaveClick(
                                                        params.row.id
                                                    )
                                                }
                                            />
                                        ) : (
                                            <FontAwesomeIcon
                                                icon={faPenToSquare}
                                                className="cursor-pointer"
                                                size="lg"
                                                onClick={() =>
                                                    handleEventEditClick(
                                                        params.row
                                                    )
                                                }
                                            />
                                        )}
                                        <FontAwesomeIcon
                                            className={`cursor-pointer trash-icon ${
                                                params.row.status !==
                                                    TimelineEventStatus.Completed &&
                                                canEventBeEditedOrDeleted(
                                                    params.row
                                                )
                                                    ? ''
                                                    : 'trash-disabled'
                                            }`}
                                            onClick={() => {
                                                if (
                                                    params.row.status !==
                                                    TimelineEventStatus.Completed
                                                ) {
                                                    if (
                                                        !canEventBeEditedOrDeleted(
                                                            params.row
                                                        )
                                                    ) {
                                                        return;
                                                    }

                                                    const docDetails =
                                                        props.documentDetailsList.find(
                                                            (d) =>
                                                                d.id ===
                                                                params.row
                                                                    .linkedAction
                                                                    ?.referenceItemId
                                                        );
                                                    if (
                                                        params.row.linkedAction
                                                            ?.actionType ===
                                                            TimelineActionType.SellerEngagementDocument &&
                                                        docDetails.type ===
                                                            ActionDocumentType.DocumentWithFeedback
                                                    ) {
                                                        const indexOfEventWithRefToThisAction =
                                                            props.rows.findIndex(
                                                                (e) =>
                                                                    e
                                                                        .linkedAction
                                                                        ?.actionReferenceId ===
                                                                    params.row
                                                                        .linkedAction
                                                                        ?.id
                                                            );
                                                        if (
                                                            indexOfEventWithRefToThisAction >
                                                            -1
                                                        ) {
                                                            createNotification(
                                                                `You can't delete this event because its action is used in the event with index ${
                                                                    indexOfEventWithRefToThisAction +
                                                                    1
                                                                }.`,
                                                                'error'
                                                            );
                                                            return;
                                                        }
                                                    }
                                                    setSelectedEventToRemoveId(
                                                        params.row.id
                                                    );
                                                    setIsConfirmationEventRemovalVisible(
                                                        true
                                                    );
                                                }
                                            }}
                                            size="lg"
                                            icon={faTrashCan}
                                        />
                                    </>
                                ) : (
                                    []
                                )}
                            </>
                        )}
                    </div>
                </div>
            ),
        },
    ];

    const canEventBeActivated = (eventId: string) => {
        const targetEvent = props.rows.find((r) => r.id === eventId);
        if (targetEvent.status !== TimelineEventStatus.Upcoming) return true;
        const eventIndex = props.rows.indexOf(targetEvent);
        const eventsBefore = props.rows.slice(0, eventIndex);
        const dependencies = targetEvent.dependencies.map((d) =>
            props.rows.find((r) => r.id === d.dependencyEventId)
        );
        const areDependenciesCompleted = dependencies.every(
            (d) => d.status === TimelineEventStatus.Completed
        );
        const arePreviousEventsUpcoming = eventsBefore.some(
            (e) => e.status === TimelineEventStatus.Upcoming
        );
        return areDependenciesCompleted && !arePreviousEventsUpcoming;
    };

    const getDocumentDetailsFromAction = (action?: TimelineAction) => {
        if (
            action?.actionType === TimelineActionType.BuyerReviewDocument ||
            action?.actionType ===
                TimelineActionType.SellerEngagementDocument ||
            action?.actionType === TimelineActionType.BuyerDocumentExecution ||
            action?.actionType === TimelineActionType.SellerBuyerSelectionIdNda
        ) {
            const document = props?.documentDetailsList?.find(
                (f) => f.id === action?.referenceItemId
            );

            return document;
        }

        return null;
    };

    const canFileBeDeleted = (
        action: TimelineAction,
        docToRemove: DocumentDetails
    ) => {
        if (action.actionType === TimelineActionType.BuyerReviewDocument) {
            return props.rows.some(
                (r) =>
                    r.party === TimelineParty.Seller &&
                    r.linkedAction?.referenceItemId === docToRemove?.id
            );
        }
        return true;
    };

    const getAdditionalDocDetailsFromMaster = (
        master: DocumentDetails
    ): DocumentDetails[] => {
        if (!master) return [];

        if (master.type != ActionDocumentType.DocumentWithFeedback) return [];
        if (master.contentType == DocumentDetailsContentType.General) return [];

        const addDocIds = new Set(master.additionalVersionIds ?? []);

        const addDocDetails = props?.documentDetailsList?.filter(
            (f) =>
                addDocIds.has(f.id) &&
                !!f.masterDocumentId &&
                f.masterDocumentId == master.id &&
                f.masterDocumentId != f.id
        );

        return addDocDetails;
    };

    const validateMeetingActionRequirements = (
        actionType: TimelineActionType
    ): boolean => {
        if (actionType === TimelineActionType.SellerBuyerAgreeOnMeetingTimes) {
            const provideMeetingAvailabilityEvent = props.rows.find(
                (event) =>
                    event.linkedAction?.actionType ===
                    TimelineActionType.SellerProvideMeetingAvailability
            );
            if (!provideMeetingAvailabilityEvent) {
                createNotification(
                    '"Provide Meeting Availability" must exist before "Agree on Meeting Times" can be added',
                    'error'
                );
                return false;
            }
        }

        if (actionType === TimelineActionType.SellerBuyerMeetingsPeriod) {
            const provideMeetingAvailabilityEvent = props.rows.find(
                (event) =>
                    event.linkedAction?.actionType ===
                    TimelineActionType.SellerProvideMeetingAvailability
            );
            const agreeOnMeetingTimesEvent = props.rows.find(
                (event) =>
                    event.linkedAction?.actionType ===
                    TimelineActionType.SellerBuyerAgreeOnMeetingTimes
            );
            if (!provideMeetingAvailabilityEvent) {
                createNotification(
                    '"Provide Meeting Availability" must exist before "Meetings Period" can be added',
                    'error'
                );
                return false;
            }
            if (!agreeOnMeetingTimesEvent) {
                createNotification(
                    '"Agree on Meeting Times" must exist before "Meetings Period" can be added',
                    'error'
                );
                return false;
            }
        }

        return true;
    };

    const linkAction = (
        newAction: TimelineAction,
        masterDocumentDetails?: DocumentDetails,
        additionalDocumentDetails?: DocumentDetails[],
        dataRequestProject?: LinkedDataRequestProject,
        ndaTermSelection?: NdaTermSelectionDocumentTemplatesState,
        expressionOfInterestSettings?: BuyerExpressionOfInterestSettings
    ) => {
        if (!validateMeetingActionRequirements(newAction.actionType)) {
            return;
        }

        if (masterDocumentDetails?.isUnregisteredFile) {
            masterDocumentDetails = {
                ...masterDocumentDetails,
                datePosted: new Date(),
            };
        }
        additionalDocumentDetails = additionalDocumentDetails?.map((d) => {
            return d.isUnregisteredFile ? { ...d, datePosted: new Date() } : d;
        });

        if (actionEvent && linkActionRowId) {
            let masterDocumentToRemove: DocumentDetails =
                getDocumentDetailsFromAction(actionEvent?.linkedAction);
            let additionalDocumentsToRemove: DocumentDetails[] =
                getAdditionalDocDetailsFromMaster(masterDocumentToRemove);

            if (!canFileBeDeleted(newAction, masterDocumentToRemove)) {
                masterDocumentToRemove = undefined;
                additionalDocumentsToRemove = [];
            }

            const newActionIsEngagementDocumentAdminUpload =
                (masterDocumentDetails?.source ===
                    EngagementDocumentSource.AdminUpload ||
                    masterDocumentDetails?.source ===
                        EngagementDocumentSource.BrokerUpload) &&
                newAction.actionType ===
                    TimelineActionType.SellerEngagementDocument;
            const newActionIsEngagementDocumentQuestionnaire =
                masterDocumentDetails?.source ===
                    EngagementDocumentSource.Questionnaire &&
                newAction.actionType ===
                    TimelineActionType.SellerEngagementDocument;

            if (newActionIsEngagementDocumentQuestionnaire) {
                if (
                    masterDocumentToRemove?.source ===
                        EngagementDocumentSource.AdminUpload ||
                    masterDocumentDetails?.source ===
                        EngagementDocumentSource.BrokerUpload
                ) {
                    masterDocumentToRemove = {
                        ...masterDocumentToRemove,
                        toRemove: true,
                    };

                    const newMasterId = generateGuid();
                    masterDocumentDetails = {
                        ...masterDocumentDetails,
                        id: newMasterId,
                        masterDocumentId: newMasterId,
                        fileName: undefined,
                        fileUrl: undefined,
                    };
                    newAction.referenceItemId = masterDocumentDetails.id;

                    additionalDocumentsToRemove =
                        additionalDocumentsToRemove.map((d) => {
                            return { ...d, toRemove: true };
                        });
                }
            }
            if (
                newAction.actionType ===
                TimelineActionType.SellerCustomSelectDataNdaTerms
            ) {
                newAction.dataNdaTermId ??= generateGuid();
                ndaTermSelection.id = newAction.dataNdaTermId;
            }

            const newActionIsMultiFile =
                newActionIsEngagementDocumentAdminUpload &&
                masterDocumentDetails?.type ==
                    ActionDocumentType.DocumentWithFeedback;

            props.handleUpdateRow(
                linkActionRowId,
                {
                    ...actionEvent,
                    linkedAction: newAction,
                },
                masterDocumentDetails,
                actionEvent?.linkedAction?.actionType ==
                    TimelineActionType.SellerEngagementDocument &&
                    (masterDocumentToRemove?.source ==
                        EngagementDocumentSource.AdminUpload ||
                        masterDocumentToRemove?.source ===
                            EngagementDocumentSource.BrokerUpload) &&
                    newActionIsEngagementDocumentAdminUpload
                    ? null
                    : masterDocumentToRemove,
                additionalDocumentDetails,
                actionEvent?.linkedAction?.actionType ==
                    TimelineActionType.SellerEngagementDocument &&
                    (masterDocumentToRemove?.source ==
                        EngagementDocumentSource.AdminUpload ||
                        masterDocumentToRemove?.source ===
                            EngagementDocumentSource.BrokerUpload) &&
                    newActionIsMultiFile
                    ? null
                    : additionalDocumentsToRemove,
                dataRequestProject,
                ndaTermSelection,
                expressionOfInterestSettings
            );

            if (linkActionRowId === editRowId) {
                setEditedEvent({ ...editedEvent, linkedAction: newAction });
            }

            setIsLinkActionPopupVisible(false);
        }
    };

    const deleteAction = (actionToRemove: TimelineAction) => {
        if (actionEvent && linkActionRowId) {
            let masterDocumentToRemove: DocumentDetails =
                getDocumentDetailsFromAction(actionToRemove);
            let additionalDocumentsToRemove: DocumentDetails[] =
                getAdditionalDocDetailsFromMaster(masterDocumentToRemove);

            if (!canFileBeDeleted(actionToRemove, masterDocumentToRemove)) {
                masterDocumentToRemove = undefined;
                additionalDocumentsToRemove = [];
            }

            masterDocumentToRemove = {
                ...masterDocumentToRemove,
                toRemove: true,
            };
            additionalDocumentsToRemove = additionalDocumentsToRemove.map(
                (d) => {
                    return { ...d, toRemove: true };
                }
            );

            props.handleUpdateRow(
                linkActionRowId,
                {
                    ...actionEvent,
                    linkedAction: null,
                },
                null,
                masterDocumentToRemove,
                null,
                additionalDocumentsToRemove
            );

            if (linkActionRowId === editRowId) {
                setEditedEvent({ ...editedEvent, linkedAction: null });
            }

            setLinkActionRowId(null);
            setActionEvent(null);
            setIsLinkActionPopupVisible(false);
        }
    };

    const getSelectedLinkedActionDocumentDetails = (
        documentId?: string
    ): DocumentDetails | null =>
        props?.documentDetailsList?.find((documentDetails) =>
            documentId
                ? documentDetails.id === documentId
                : actionEvent?.linkedAction?.referenceItemId ===
                  documentDetails.id
        ) ?? null;

    const getDataRequestProject = (
        id: string
    ): LinkedDataRequestProject | null => {
        return props?.dataRequestProjects?.find((p) => p.id === id) ?? null;
    };

    return (
        <>
            <Box
                className="timeline-table"
                style={{
                    minHeight: '400px',
                    width: '100%',
                    overflow: 'auto',
                    scrollbarWidth: 'none',
                }}>
                <DataGrid
                    disableColumnFilter
                    disableColumnSorting
                    disableRowSelectionOnClick
                    disableColumnSelector
                    disableColumnMenu
                    disableDensitySelector
                    rows={props.rows.map((row, index) => ({
                        ...row,
                        rowIndex: index,
                    }))}
                    getRowHeight={(params) => {
                        const row = params.model as TimelineEvent;
                        const baseHeight = 58.6;
                        const dependencyHeight = 15;
                        const padding = 5;
                        const additionalHeightForEdit = 75;

                        const dependencies =
                            row.id === editRowId && editedEvent
                                ? editedEvent.dependencies || []
                                : row.dependencies || [];

                        const isEditingAndNeedsExtraHeight =
                            row.id === editRowId && dependencies.length > 0;

                        return dependencies.length > 0
                            ? baseHeight +
                                  dependencies.length * dependencyHeight +
                                  padding +
                                  (isEditingAndNeedsExtraHeight
                                      ? additionalHeightForEdit
                                      : 0)
                            : baseHeight +
                                  (isEditingAndNeedsExtraHeight
                                      ? additionalHeightForEdit
                                      : 0);
                    }}
                    getRowId={(row) => row.id}
                    columns={columns}
                    sx={finalStyles}
                    loading={props.isLoading}
                />
            </Box>
            {isLinkActionPopupVisible ? (
                <LinkActionPopup
                    documentDetailsList={props.documentDetailsList}
                    documentsSections={props.documentsSections}
                    timelineId={props.timelineId}
                    isTemplate={props.isTemplate}
                    mode={linkActionPopupMode}
                    timelineEvents={props.rows}
                    close={() => {
                        setIsLinkActionPopupVisible(false);
                    }}
                    dataRequestProjects={props.dataRequestProjects}
                    party={actionEvent.party}
                    linkAction={linkAction}
                    deleteAction={deleteAction}
                    linkedAction={actionEvent.linkedAction}
                    currentEvent={actionEvent}
                    getDocumentDetails={getSelectedLinkedActionDocumentDetails}
                    getDataRequestProject={getDataRequestProject}
                    teaserData={props.teaserData}
                    ndaTermSelectionState={props.ndaTermSelectionState}
                    onTeaserSave={props.onTeaserSave}
                    isTeaserConfirmed={props.isTeaserConfirmed}
                    onUpdateConfirm={props.onUpdateConfirm}
                    setIsConfirmationActionRemovalVisible={
                        setIsConfirmationActionRemovalVisible
                    }
                    setDocumentSectionList={props.setDocumentSectionList}
                    expressionOfInterestSettings={
                        props.expressionOfInterestSettings
                    }
                    levelOfService={props.levelOfService}
                    isMeetingActionEditOrDeleteDisabled={isMeetingActionEditOrDeleteDisabled(
                        actionEvent.linkedAction
                    )}
                />
            ) : (
                <></>
            )}
            {statusToConfirm ? (
                <ConfirmationModal
                    isVisible={statusToConfirm}
                    setIsVisible={() => setStatusToConfirm(false)}
                    header="Finalizing Engagement"
                    confirmationText={`After selecting this status and clicking "Save", the Engagement will be marked as Completed, and all related data will be read-only. This action cannot be undone.`}
                    onConfirm={() => {
                        handleInputChange(
                            'status',
                            TimelineEventStatus.Completed
                        );
                        setStatusToConfirm(false);
                    }}
                    params={undefined}
                    nextButtonText="Save"
                    cancelButtonText="Cancel"
                />
            ) : (
                []
            )}
            <ConfirmationModal
                isVisible={isConfirmationActionRemovalVisible}
                setIsVisible={() => {
                    setIsConfirmationActionRemovalVisible(false);
                }}
                header={'Are you sure?'}
                confirmationText={
                    'Are you sure you want to delete linked action?'
                }
                onConfirm={() => deleteAction(actionEvent.linkedAction)}
                params={undefined}
                cancelButtonText={'No, take me back'}
                nextButtonText={`Yes, I'm sure`}
            />
            <ConfirmationModal
                isVisible={isConfirmationEventRemovalVisible}
                setIsVisible={() => {
                    setIsConfirmationEventRemovalVisible(false);
                }}
                header={'Are you sure?'}
                confirmationText={'Are you sure you want to delete event?'}
                onConfirm={() =>
                    handleRemoveRowWithDependencies(selectedEventToRemoveId)
                }
                params={undefined}
                cancelButtonText={'No, take me back'}
                nextButtonText={`Yes, I'm sure`}
            />
            <ConfirmationModal
                isVisible={isConfirmationDependencyRemovalVisible}
                setIsVisible={() => {
                    setIsConfirmationDependencyRemovalVisible(false);
                }}
                header={'Are you sure?'}
                confirmationText={'Are you sure you want to remove dependency?'}
                onConfirm={() =>
                    handleRemoveDependencyById(selectedDependencyIdForRemoval)
                }
                params={undefined}
                cancelButtonText={'No, take me back'}
                nextButtonText={`Yes, I'm sure`}
            />
        </>
    );
};

export default TimelineTable;
